import React, { useState, useEffect, useCallback, useContext } from 'react';
import { collection, getDocs, doc, deleteDoc, getDoc, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import PremiumGuard from './PremiumGuard';

// Message Box Component with updated theme
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Helper function to calculate age
const calculateAge = (dobDate) => {
    if (!dobDate) return 'N/A';
    const diff = Date.now() - dobDate.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export default function ShortlistPage() {
    const { userId, db, isAuthReady } = useContext(AuthContext);
    const [shortlistedUsers, setShortlistedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    // Function to fetch shortlisted users' profiles
    const fetchShortlistedUsers = useCallback(async () => {
        if (!isAuthReady || !userId || !db) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const shortlistRef = collection(db, "shortlists");
            const q = query(shortlistRef, where("likerId", "==", userId));
            const shortlistSnapshot = await getDocs(q);
            
            const shortlistedUserIds = shortlistSnapshot.docs.map(doc => ({
                id: doc.data().likedUserId,
                addedAt: doc.data().addedAt,
                shortlistDocId: doc.id
            }));

            const fetchedProfiles = [];
            if (shortlistedUserIds.length > 0) {
                for (const shortlistEntry of shortlistedUserIds) {
                    try {
                        let profileDocRef = doc(db, "users", shortlistEntry.id);
                        let profileDocSnap = await getDoc(profileDocRef);
                        
                        if (!profileDocSnap.exists()) {
                            profileDocRef = doc(db, "profiles", shortlistEntry.id);
                            profileDocSnap = await getDoc(profileDocRef);
                        }
                        
                        if (profileDocSnap.exists()) {
                            const data = profileDocSnap.data();
                            fetchedProfiles.push({
                                id: profileDocSnap.id,
                                firstName: data.firstName || '',
                                lastName: data.lastName || '',
                                dob: data.dob && data.dob.toDate ? data.dob.toDate() : 
                                     data.dateOfBirth ? new Date(data.dateOfBirth) : null,
                                country: data.country || '',
                                state: data.state || '',
                                city: data.city || '',
                                gender: data.gender || '',
                                profession: data.profession || '',
                                education: data.education || '',
                                church: data.church || '',
                                profileSummary: data.profileSummary || data.bio || '',
                                photo: data.photo || data.profilePhotoUrl || '',
                                addedAt: shortlistEntry.addedAt,
                                shortlistDocId: shortlistEntry.shortlistDocId
                            });
                        }
                    } catch (profileError) {
                        console.error(`Error fetching profile for user ${shortlistEntry.id}:`, profileError);
                    }
                }
                
                fetchedProfiles.sort((a, b) => {
                    if (!a.addedAt && !b.addedAt) return 0;
                    if (!a.addedAt) return 1;
                    if (!b.addedAt) return -1;
                    return b.addedAt.toDate().getTime() - a.addedAt.toDate().getTime();
                });
            }
            setShortlistedUsers(fetchedProfiles);

        } catch (error) {
            console.error("Error fetching shortlisted users:", error);
            setMessage(`Error loading shortlisted profiles: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [isAuthReady, userId, db]);

    useEffect(() => {
        if (isAuthReady && userId) {
            fetchShortlistedUsers();
        } else if (isAuthReady && !userId) {
            setLoading(false);
            setShortlistedUsers([]);
            setMessage('Please log in to view your shortlist.');
        }
    }, [isAuthReady, userId, fetchShortlistedUsers]);

    const handleRemoveFromShortlist = async (targetUserId) => {
        if (!userId) {
            setMessage('You must be logged in to perform this action.');
            return;
        }

        try {
            const userToRemove = shortlistedUsers.find(user => user.id === targetUserId);
            if (!userToRemove || !userToRemove.shortlistDocId) {
                setMessage('Could not find shortlist entry to remove.');
                return;
            }

            const docRef = doc(db, "shortlists", userToRemove.shortlistDocId);
            await deleteDoc(docRef);

            setShortlistedUsers(prev => prev.filter(user => user.id !== targetUserId));

            setMessage("Profile removed from your shortlist.");
        } catch (error) {
            console.error("Error removing from shortlist:", error);
            setMessage(`Could not remove from shortlist: ${error.message}`);
        }
    };

    const handleViewProfile = (targetUserId) => {
        navigate(`/profile/${targetUserId}`);
    };

    const calculateAgeFromDate = (dateOfBirth) => {
        if (!dateOfBirth) return 'N/A';
        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    if (!isAuthReady) {
        return (
            <div className="container mx-auto px-6 py-8 mt-20">
                <h2 className="text-4xl font-extrabold mb-6 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Loading...</h2>
                <p className="text-lg text-gray-600 text-center">Initializing authentication...</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container mx-auto px-6 py-8 mt-20">
                <h2 className="text-4xl font-extrabold mb-2 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Your Shortlisted Profiles</h2>
                <p className="text-lg text-gray-600 mb-8 text-center">These are the profiles you've added to your shortlist</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(8)].map((_, index) => (
                        <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
                            <div className="h-48 w-full bg-gray-200 animate-pulse"></div>
                            <div className="p-4 flex-grow flex flex-col">
                                <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-3/4"></div>
                                <div className="h-4 bg-gray-200 rounded animate-pulse mb-4 w-1/2"></div>
                                <div className="h-16 bg-gray-200 rounded animate-pulse mb-4"></div>
                                <div className="mt-auto pt-4 border-t border-gray-100 space-y-2">
                                    <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <PremiumGuard>
            <div className="container mx-auto px-6 py-8 mt-20">
            <h2 className="text-4xl font-extrabold mb-2 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Your Shortlisted Profiles</h2>
            <p className="text-lg text-gray-600 mb-8 text-center">These are the profiles you've added to your shortlist</p>

            {message && <MessageBox message={message} onClose={() => setMessage('')} />}

            {shortlistedUsers.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl shadow-xl text-center">
                    <div className="mx-auto mb-4 h-16 w-16 text-gray-400 flex items-center justify-center rounded-full" style={{ background: 'hsl(220, 15%, 96%)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                        No Profiles in Shortlist
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Explore the Discover page to add profiles to your shortlist!
                    </p>
                    <button
                        onClick={fetchShortlistedUsers}
                        className="px-6 py-3 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                        style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        Refresh
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {shortlistedUsers.map((user) => (
                        <div key={user.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                            <div className="relative h-56 w-full bg-gray-200 flex items-center justify-center">
                                <img
                                    src={user.photo || "https://placehold.co/256x256/1e293b/ffffff?text=No+Photo&font=raleway"}
                                    alt={`${user.firstName} ${user.lastName}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { 
                                        e.target.onerror = null; 
                                        e.target.src = "https://placehold.co/256x256/1e293b/ffffff?text=No+Photo&font=raleway"; 
                                    }}
                                />
                                <div className="absolute top-3 right-3 p-2.5 rounded-full shadow-lg text-white" 
                                     style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="p-3 flex-grow flex flex-col">
                                <h3 className="text-lg font-bold mb-1" style={{ color: 'hsl(221, 45%, 18%)' }}>
                                    {user.firstName} {user.lastName}
                                </h3>
                                <p className="text-gray-600 text-sm mb-1">
                                    {user.gender || 'Not specified'}, {user.dob ? calculateAge(user.dob) : calculateAgeFromDate(user.dateOfBirth)} years
                                </p>
                                <p className="text-gray-600 text-sm mb-1">
                                    {user.city || 'City not specified'}{user.state && `, ${user.state}`}{user.country && `, ${user.country}`}
                                </p>
                                {user.church && (
                                    <p className="text-gray-600 text-sm mb-1">
                                        Church: {user.church}
                                    </p>
                                )}
                                <p className="text-gray-700 text-xs mb-2 line-clamp-2">
                                    {user.profileSummary || 'No profile summary provided.'}
                                </p>

                                <div className="text-xs text-gray-500 mb-2">
                                    {user.profession && <span>Profession: {user.profession}</span>}
                                    {user.education && <div>Education: {user.education}</div>}
                                </div>

                                {user.addedAt && (
                                    <p className="text-xs mb-2 font-medium" style={{ color: 'hsl(43, 74%, 52%)' }}>
                                        Added: {user.addedAt.toDate().toLocaleDateString()}
                                    </p>
                                )}

                                <div className="mt-auto pt-2 border-t border-gray-100 space-y-1.5">
                                    <button
                                        onClick={() => handleViewProfile(user.id)}
                                        className="w-full px-4 py-2 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                                        style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        View Profile
                                    </button>
                                    
                                    <button
                                        onClick={() => handleRemoveFromShortlist(user.id)}
                                        className="w-full px-4 py-2 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                                        style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Remove from Shortlist
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </div>
        </PremiumGuard>
    );
}